import type { CountryCodeValue } from '../../types/localization/localizationHelperTypes';

export type DomainsByCountry = Partial<Record<CountryCodeValue, string>>;

/**
 * Country → host map for the environment the application runs in, covering every market it serves.
 *
 * The values originate from `runtimeConfig.public.domains`, which Nitro fills on start-up from
 * `NUXT_PUBLIC_DOMAINS` / `NUXT_PUBLIC_DOMAINS_<COUNTRY>`. Local defaults live in `.env`, the
 * deployed ones in the environment `app.yaml`.
 *
 * Reading that config through `useRuntimeConfig()` needs a Nuxt instance, and these getters are
 * called from places that do not have one: Nitro plugins and server utils run outside the Vue app
 * entirely, and on the render path `useHead` evaluates computed meta values once setup has already
 * finished. So the map is snapshotted where an instance is available and read from here as plain
 * data. It takes two plugins because the two runtimes get the values from different places:
 * `server/plugins/domains` reads them out of the Nitro config once per process,
 * `app/plugins/domains.client` out of the payload once per browser app.
 *
 * The snapshot is kept on `globalThis` on purpose. On the server the Nitro bundle and the Vue SSR
 * bundle are separate module graphs sharing one process, so a module-level variable gives each of
 * them its own copy and only the Nitro one ever gets filled — which shows up as every country
 * falling back to the default locale during rendering.
 */
const DOMAINS_SNAPSHOT_KEY = '__expondoDomains__';

const snapshotHolder = globalThis as typeof globalThis & {
  [DOMAINS_SNAPSHOT_KEY]?: DomainsByCountry;
};

export const setDomains = (domains: DomainsByCountry | undefined): void => {

  snapshotHolder[DOMAINS_SNAPSHOT_KEY] = { ...domains };

};

export const getDomains = (): DomainsByCountry => {

  return snapshotHolder[DOMAINS_SNAPSHOT_KEY] ?? {};

};

export const getDomainByCountry = (country: CountryCodeValue): string => {

  return getDomains()[country] ?? '';

};

export const getAllDomains = (): string[] => {

  return Object.values(getDomains()).filter((domain): domain is string => Boolean(domain));

};

export const getCountriesWithoutDomain = (): string[] => {

  return Object.entries(getDomains())
    .filter(([, domain]) => !domain)
    .map(([country]) => country);

};
